<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import {
  referenceOfferStatusByStep,
  REFERENCE_OFFER_MACRO_STATUS,
} from '../utils/referenceOfferLogic';

const props = defineProps({
  offer: { type: Object, required: true },
  buyerLabel: { type: String, default: '' },
  statusLabel: { type: String, default: '' },
  statusDetail: { type: String, default: '' },
  reference: { type: [String, Number], default: '' },
  formattedAmount: { type: String, default: '' },
  rentProgressActions: { type: Array, default: () => [] },
  rentProgressSummaries: { type: Array, default: () => [] },
});

const emit = defineEmits(['open', 'rentProgressAction']);

const thumbnailUrl = computed(
  () =>
    props.offer.thumbnail_url ||
    props.offer.cover_url ||
    props.offer.imovel_thumbnail_url ||
    props.offer.imovel_cover_url ||
    props.offer.imovel?.cover_url ||
    props.offer.imovel?.thumbnail_url ||
    ''
);

const offerStepKey = computed(
  () =>
    props.offer.current_step?.key ||
    props.offer.currentStep?.key ||
    props.offer.step?.key ||
    props.offer.sales_flow_step ||
    props.offer.salesFlowStep ||
    props.offer.status
);

const localMacroStatusByStatus = computed(
  () =>
    ({
      submitted: REFERENCE_OFFER_MACRO_STATUS.NEW,
      accepted: REFERENCE_OFFER_MACRO_STATUS.OFFER_ACCEPTED,
      partially_signed: REFERENCE_OFFER_MACRO_STATUS.CCV,
      signed: REFERENCE_OFFER_MACRO_STATUS.FINISHED,
      declined: REFERENCE_OFFER_MACRO_STATUS.CANCELED,
      withdrawn: REFERENCE_OFFER_MACRO_STATUS.CANCELED,
    })[props.offer.status] || null
);

const macroStatus = computed(
  () =>
    referenceOfferStatusByStep(
      offerStepKey.value,
      props.offer.ccv || props.offer.ccv_contract
    ) || localMacroStatusByStatus.value
);

const statusIcon = computed(
  () =>
    ({
      [REFERENCE_OFFER_MACRO_STATUS.NEW]: 'i-lucide-file-text',
      [REFERENCE_OFFER_MACRO_STATUS.NEGOTIATION]: 'i-lucide-message-circle',
      [REFERENCE_OFFER_MACRO_STATUS.OFFER_ACCEPTED]: 'i-lucide-circle-check',
      [REFERENCE_OFFER_MACRO_STATUS.CCV]: 'i-lucide-file-signature',
      [REFERENCE_OFFER_MACRO_STATUS.FINISHED]: 'i-lucide-party-popper',
      [REFERENCE_OFFER_MACRO_STATUS.CANCELED]: 'i-lucide-circle-x',
    })[macroStatus.value] || 'i-lucide-file-text'
);

const statusTone = computed(
  () =>
    ({
      [REFERENCE_OFFER_MACRO_STATUS.FINISHED]: 'bg-emerald-50 text-emerald-700',
      [REFERENCE_OFFER_MACRO_STATUS.CANCELED]: 'bg-rose-50 text-rose-700',
    })[macroStatus.value] || 'bg-lais-primary-50 text-lais-primary'
);

const stepLabel = computed(
  () =>
    (props.rentProgressActions.length && props.statusDetail) ||
    {
      [REFERENCE_OFFER_MACRO_STATUS.NEW]: 'Nova proposta',
      [REFERENCE_OFFER_MACRO_STATUS.NEGOTIATION]: 'Em negociação',
      [REFERENCE_OFFER_MACRO_STATUS.OFFER_ACCEPTED]: 'Proposta aceita',
      [REFERENCE_OFFER_MACRO_STATUS.CCV]: 'CCV',
      [REFERENCE_OFFER_MACRO_STATUS.FINISHED]: 'Finalizada',
      [REFERENCE_OFFER_MACRO_STATUS.CANCELED]: 'Cancelada',
    }[macroStatus.value] ||
    props.statusDetail ||
    props.statusLabel ||
    'Nova proposta'
);

const propertyCode = computed(
  () =>
    props.offer.imovel?.external_id ||
    props.offer.imovel?.externalId ||
    props.offer.imovel?.short_id ||
    props.offer.imovel?.shortId ||
    props.offer.imovel_id ||
    props.offer.house_id ||
    props.offer.houseId ||
    props.reference ||
    '-'
);

const primaryActions = computed(() =>
  props.rentProgressActions.filter(action => action.tone === 'primary')
);
const secondaryActions = computed(() =>
  props.rentProgressActions.filter(action => action.tone !== 'primary')
);

const commissionBreakdown = computed(
  () =>
    props.offer.commission_breakdown ||
    props.offer.commissionBreakdown ||
    props.offer.metadata?.commission_breakdown ||
    {}
);

const rentalBreakdown = computed(
  () =>
    props.offer.metadata?.rental_breakdown || props.offer.rental_breakdown || {}
);

const contract = computed(
  () =>
    props.offer.ccv_contract ||
    props.offer.ccv ||
    (Array.isArray(props.offer.property_contracts)
      ? props.offer.property_contracts.find(item =>
          ['ccv', 'rental_contract', 'rental'].includes(
            String(item?.kind || '').toLowerCase()
          )
        )
      : null)
);

const numberValue = value => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const formatCurrency = value => {
  const number = numberValue(value);
  if (number === null) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(number);
};

const formatPercentage = value => {
  const number = numberValue(value);
  if (number === null) return '';
  return `${new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: 2,
  }).format(number)}%`;
};

const hasCommissionSplit = computed(() =>
  Boolean(
    props.offer.rent_commission_split ||
      props.offer.rentCommissionSplit ||
      props.offer.metadata?.rent_commission_split ||
      contract.value?.metadata?.rent_commission_split
  )
);

const commissionRows = computed(() =>
  [
    {
      label: commissionBreakdown.value.source_label || 'Taxa de administração',
      value: formatCurrency(commissionBreakdown.value.value),
    },
    {
      label: 'Percentual',
      value: formatPercentage(
        commissionBreakdown.value.percentage ||
          rentalBreakdown.value.admin_fee_percentage
      ),
    },
    {
      label: 'Proprietário recebe',
      value: formatCurrency(rentalBreakdown.value.owner_receive_amount),
    },
  ].filter(row => row.value)
);

const emitRentProgressAction = action =>
  emit('rentProgressAction', {
    action: action.key,
    offer: props.offer,
  });

const summaryToneClass = status =>
  ({
    completed: 'bg-emerald-50 text-emerald-700',
    waiting: 'bg-amber-50 text-amber-700',
    terminated: 'bg-rose-50 text-rose-700',
  })[status] || 'bg-lais-primary-50 text-lais-primary';
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <article
    class="mx-auto w-full max-w-[615px] overflow-hidden rounded-lais border border-lais-border bg-white shadow-sm transition hover:border-lais-primary/40 hover:shadow-lais-card"
  >
    <div class="flex min-w-0 gap-4 p-4">
      <div
        class="grid size-[120px] shrink-0 place-items-center overflow-hidden rounded-lais-sm bg-lais-bg-soft text-lais-primary"
      >
        <img
          v-if="thumbnailUrl"
          :src="thumbnailUrl"
          :alt="offer.imovel_titulo || 'Imóvel da proposta'"
          class="h-full w-full object-cover"
        />
        <template v-else>
          <span class="i-lucide-building-2 size-9" aria-hidden="true" />
        </template>
      </div>

      <div class="min-w-0 flex-1">
        <p class="truncate text-[15px] font-semibold text-lais-text">
          {{ buyerLabel || 'Cliente não vinculado' }}
        </p>
        <p class="mt-1 truncate text-[13px] text-lais-text-body">
          Imóvel: {{ propertyCode }}
        </p>
        <p class="mt-3 text-[13px] text-lais-text-muted">Valor da proposta</p>
        <p class="mt-1 text-[20px] font-semibold text-lais-text">
          {{ formattedAmount }}
        </p>
      </div>
    </div>

    <div
      class="grid gap-3 border-t border-lais-border-soft px-4 py-3 sm:grid-cols-[1fr_auto] sm:items-center"
    >
      <button
        type="button"
        class="flex min-w-0 items-center gap-3 text-left transition-colors hover:text-lais-primary"
        @click="emit('open', offer)"
      >
        <span
          class="grid size-9 shrink-0 place-items-center rounded-full"
          :class="statusTone"
        >
          <span class="size-4" :class="statusIcon" aria-hidden="true" />
        </span>
        <span class="min-w-0">
          <span class="block text-[13px] text-lais-text-muted">
            Status da proposta
          </span>
          <span
            class="block truncate text-[14px] font-semibold"
            :class="
              macroStatus === REFERENCE_OFFER_MACRO_STATUS.CANCELED
                ? 'text-rose-700'
                : 'text-lais-text'
            "
          >
            {{ stepLabel }}
          </span>
        </span>
      </button>

      <div class="flex flex-wrap gap-2 sm:justify-end">
        <button
          v-for="action in primaryActions"
          :key="action.key"
          type="button"
          class="inline-flex h-9 items-center justify-center gap-1.5 rounded-full bg-lais-primary px-3 text-[12px] font-semibold text-white transition hover:bg-lais-primary-600"
          @click="emitRentProgressAction(action)"
        >
          <span class="size-3.5" :class="action.icon" aria-hidden="true" />
          {{ action.label }}
        </button>
        <button
          v-for="action in secondaryActions"
          :key="action.key"
          type="button"
          class="grid size-9 place-items-center rounded-full border border-lais-border-soft bg-white text-lais-primary transition hover:border-lais-primary hover:bg-lais-primary-50"
          :aria-label="action.label"
          :title="action.label"
          @click="emitRentProgressAction(action)"
        >
          <span class="size-4" :class="action.icon" aria-hidden="true" />
        </button>
      </div>
    </div>

    <div
      v-if="rentProgressSummaries.length"
      class="border-t border-lais-border-soft bg-lais-bg-soft/50 px-4 py-3"
    >
      <p class="text-[12px] font-semibold text-lais-text">
        Resumo da negociação
      </p>
      <ul class="mt-2 grid gap-2">
        <li
          v-for="summary in rentProgressSummaries"
          :key="summary.key"
          class="flex min-w-0 items-start gap-2"
        >
          <span
            class="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full"
            :class="summaryToneClass(summary.status)"
            aria-hidden="true"
          >
            <span class="size-3.5" :class="summary.icon" />
          </span>
          <span class="min-w-0">
            <span class="block text-[11px] font-semibold text-lais-text-muted">
              {{ summary.role }}
            </span>
            <span class="block text-[12px] leading-5 text-lais-text-body">
              {{ summary.text }}
            </span>
          </span>
        </li>
      </ul>
    </div>

    <div
      v-if="hasCommissionSplit"
      class="border-t border-lais-border-soft bg-lais-primary-50/40 px-4 py-3"
    >
      <div class="flex gap-3">
        <span
          class="grid size-8 shrink-0 place-items-center rounded-full bg-white text-lais-primary"
          aria-hidden="true"
        >
          <span class="i-lucide-users-round size-4" />
        </span>
        <div class="min-w-0 flex-1">
          <p class="text-[13px] font-semibold text-lais-text">
            Comissionamento dividido
          </p>
          <p class="mt-1 text-[12px] leading-5 text-lais-text-body">
            A pessoa interessada também visitou este imóvel com outro corretor e
            o comissionamento será dividido entre vocês.
          </p>
          <dl
            v-if="commissionRows.length"
            class="mt-3 grid gap-2 sm:grid-cols-3"
          >
            <div v-for="row in commissionRows" :key="row.label" class="min-w-0">
              <dt class="truncate text-[11px] text-lais-text-muted">
                {{ row.label }}
              </dt>
              <dd class="mt-0.5 text-[12px] font-semibold text-lais-text">
                {{ row.value }}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  </article>
</template>
